import {NavigateFunction} from 'react-router-dom';
import {savaquanFetchJson} from '../../shared/services/http.service';
import {ExternalCompanyDto} from '../../shared/dtos/company.dto';
import {HoursWorkedWeekDto, MyProfileDto} from './account-model';

/*
 * The Account page's endpoints (SQ-404), the ones Android's AccountViewModel
 * calls: the person's own details and hours (any signed-in person), and the
 * business details and logo (rbac.manage, the owner).
 */

const base = () => `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1`;

const putJson = <T>(navigate: NavigateFunction, path: string, body: unknown) =>
  savaquanFetchJson<T>(`${base()}${path}`, {
    navigate,
    method: 'PUT',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(body),
  });

export const getMyProfile = (navigate: NavigateFunction) =>
  savaquanFetchJson<MyProfileDto>(`${base()}/users/me/profile`, {navigate});

export interface UpdateMyProfileRequest {
  name: string;
  phoneNumber: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export const updateMyProfile = (navigate: NavigateFunction, request: UpdateMyProfileRequest) =>
  putJson<MyProfileDto>(navigate, '/users/me/profile', request);

export const updateMyHoursWorked = (navigate: NavigateFunction, hoursWorked: HoursWorkedWeekDto[]) =>
  putJson<MyProfileDto>(navigate, '/users/me/hours-worked', {hoursWorked});

export const getMyCompany = (navigate: NavigateFunction) =>
  savaquanFetchJson<ExternalCompanyDto>(`${base()}/companies/me`, {navigate});

export interface UpdateCompanyRequest {
  name: string;
  phoneNumber: string | null;
  email: string | null;
  billingStreetAddress1: string | null;
  billingStreetAddress2: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
}

export const updateMyCompany = (navigate: NavigateFunction, request: UpdateCompanyRequest) =>
  putJson<ExternalCompanyDto>(navigate, '/companies/me', request);

/** One PNG "file" part, as Android's uploadLogo. */
export const uploadLogo = (navigate: NavigateFunction, png: Blob) => {
  const form = new FormData();
  form.append('file', png, 'logo.png');
  return savaquanFetchJson<ExternalCompanyDto>(`${base()}/companies/me/logo`, {navigate, method: 'PUT', body: form});
};

export const removeLogo = (navigate: NavigateFunction) =>
  savaquanFetchJson<ExternalCompanyDto>(`${base()}/companies/me/logo`, {navigate, method: 'DELETE'});

/** What the phones scale a logo to (LogoImport.MAX_SIDE); the server fits it to the same. */
const LOGO_MAX_SIDE = 1200;

export class UnreadableLogo extends Error {}

/**
 * Android's LogoImport, as far as a browser goes: whatever image is picked
 * (PNG, JPEG, WebP, GIF, SVG…) is drawn once and handed over as one PNG, so
 * a transparent logo stays transparent, fitted within 1200px. A browser
 * cannot draw a PDF or (outside Safari) a HEIC photo; those are refused
 * with a message saying what to pick instead.
 */
export const logoToPng = async (file: File): Promise<Blob> => {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new UnreadableLogo());
      img.src = url;
    });
    // An SVG without its own size reports 0; draw it at the logo size.
    const width = image.naturalWidth || LOGO_MAX_SIDE;
    const height = image.naturalHeight || LOGO_MAX_SIDE;
    const scale = Math.min(1, LOGO_MAX_SIDE / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
    const png = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
    if (!png) throw new UnreadableLogo();
    return png;
  } catch (e) {
    if (e instanceof UnreadableLogo) {
      throw new UnreadableLogo('That file could not be read as a picture. Try a PNG, JPG or SVG of your logo.');
    }
    throw e;
  } finally {
    URL.revokeObjectURL(url);
  }
};
